// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState, useCallback, useRef, useEffect } from 'react';

import { SubscriberAndCallbacksFor } from './useMethods';
import { ConditionallyMergeRecordTypes } from './utilityTypes';

type CollectorMethods<S extends SubscriberAndCallbacksFor<unknown, unknown>> = {
    actions: S['actions'];
    query: S['query'];
};
// eslint-disable-next-line
export type useCollectorReturnType<
    S extends SubscriberAndCallbacksFor<any, unknown>,
    C = null,
> = ConditionallyMergeRecordTypes<C, CollectorMethods<S>>;

// eslint-disable-next-line
export function useCollector<S extends SubscriberAndCallbacksFor<any, unknown>, C>(
    store: S,
    collector?: (state: ReturnType<S['getState']>['current'], query: S['query']) => C,
): useCollectorReturnType<S, C> {
    const { subscribe, getState, actions, query } = store;

    const initial = useRef(true);
    const collected = useRef<C | null>(null);
    const collectorRef = useRef(collector);
    collectorRef.current = collector;

    const onCollect = useCallback(
        (collected: C | null) => {
            return { ...(collected ?? {}), actions, query } as useCollectorReturnType<S, C>;
        },
        [actions, query],
    );

    // Collect states for initial render
    if (initial.current && collector) {
        collected.current = collector(getState(), query);
        initial.current = false;
    }

    const [renderCollected, setRenderCollected] = useState<useCollectorReturnType<S, C>>(onCollect(collected.current));

    // Collect states on state change
    useEffect(() => {
        let unsubscribe: () => void;
        if (collectorRef.current) {
            unsubscribe = subscribe(
                (current) => {
                    return collectorRef.current && collectorRef.current(current, query);
                },
                (collected) => {
                    setRenderCollected(onCollect(collected ?? null));
                },
            );
        }
        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [onCollect, query, subscribe]);

    return renderCollected;
}
